// components/GuestlistRsvpModal.js
//
// RSVP for someone without an account: email → 6-digit code → name and
// password → signed in, and the RSVP they started goes through.
//
// Email, like the main signup: codes reach every country, and the auth-codes
// Edge Function hashes them, caps attempts and rate-limits sends on the
// server. A phone number is only needed later, to create invitations.
//
// The username is made up from their name — nobody RSVPing to a birthday
// wants to pick a handle — but the password is theirs, so the account is one
// they can sign back into rather than a throwaway.
import React, { useState, useRef, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { FiX, FiMail, FiUser, FiLock, FiCheck, FiArrowLeft } from 'react-icons/fi';
import supabase from '../services/supabase';
import {
  sendSignupEmailCode,
  verifySignupEmailCode,
  completeEmailSignup,
} from '../services/authCodes';
import '../styles/Modals.scss';

const RESEND_COOLDOWN = 30; // seconds
const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/;
const USERNAME_ATTEMPTS = 4;

/** Jane Doe -> "janedoe4821"; fits the server's 3–30 letters/digits rule. */
const makeUsername = (first, last) => {
  const base = `${first}${last}`.toLowerCase().replace(/[^a-z0-9]/g, '').slice(0, 20) || 'guest';
  return `${base.length < 3 ? `${base}guest` : base}${Math.floor(1000 + Math.random() * 9000)}`;
};

const GuestlistRsvpModal = ({ onClose, onAuthenticated, eventTitle }) => {
  const navigate = useNavigate();
  const location = useLocation();
  const [step, setStep] = useState('email'); // 'email' | 'otp' | 'name'
  const [email, setEmail] = useState('');
  const [otp, setOtp] = useState(['', '', '', '', '', '']);
  const [signupToken, setSignupToken] = useState(null);
  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState('');
  const [password, setPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [resendTimer, setResendTimer] = useState(0);

  const timerRef = useRef(null);
  const otpRefs = useRef([]);

  const normalisedEmail = email.trim().toLowerCase();
  const emailValid = EMAIL_RE.test(normalisedEmail);

  useEffect(() => () => { if (timerRef.current) clearInterval(timerRef.current); }, []);

  const startResendTimer = () => {
    setResendTimer(RESEND_COOLDOWN);
    if (timerRef.current) clearInterval(timerRef.current);
    timerRef.current = setInterval(() => {
      setResendTimer(prev => {
        if (prev <= 1) { clearInterval(timerRef.current); return 0; }
        return prev - 1;
      });
    }, 1000);
  };

  const goToLogin = () => {
    onClose();
    navigate('/login', { state: { redirectTo: location.pathname, email: normalisedEmail } });
  };

  const handleSendCode = async (e) => {
    if (e) e.preventDefault();
    if (!emailValid) { setError('Please enter a valid email address'); return; }

    setError(null);
    setLoading(true);
    try {
      await sendSignupEmailCode(normalisedEmail);
      startResendTimer();
      setOtp(['', '', '', '', '', '']);
      setStep('otp');
      setTimeout(() => otpRefs.current[0]?.focus(), 100);
    } catch (err) {
      // They already have an account: sign in, and come back to this page.
      if (err.code === 'account_exists') { goToLogin(); return; }
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const handleVerifyCode = async (e) => {
    if (e) e.preventDefault();
    const code = otp.join('');
    if (code.length !== 6) { setError('Please enter the 6-digit code'); return; }

    setError(null);
    setLoading(true);
    try {
      setSignupToken(await verifySignupEmailCode(normalisedEmail, code));
      setStep('name');
      setTimeout(() => document.getElementById('rsvp-first-name')?.focus(), 100);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const handleNameSubmit = async (e) => {
    if (e) e.preventDefault();
    if (!firstName.trim()) { setError('Please enter your first name'); return; }
    if (password.length < 8) { setError('Use at least 8 characters for your password'); return; }

    setError(null);
    setLoading(true);
    try {
      // A made-up username can collide; try a few before giving up.
      let created = false;
      for (let i = 0; i < USERNAME_ATTEMPTS && !created; i += 1) {
        try {
          await completeEmailSignup({
            email: normalisedEmail,
            signupToken,
            username: makeUsername(firstName, lastName),
            password,
          });
          created = true;
        } catch (err) {
          if (err.code !== 'username_taken' || i === USERNAME_ATTEMPTS - 1) throw err;
        }
      }

      const { data: signInData, error: signInError } = await supabase.auth.signInWithPassword({
        email: normalisedEmail,
        password,
      });
      if (signInError) throw new Error('Your account was created, but signing in failed. Please sign in with your email.');

      if (signInData?.user) {
        await supabase.from('users').update({
          first_name: firstName.trim(),
          last_name: lastName.trim() || null,
          updated_at: new Date().toISOString(),
        }).eq('id', signInData.user.id);

        onAuthenticated(signInData.user.id);
      }
    } catch (err) {
      if (err.code === 'token_expired') {
        setStep('email');
        setOtp(['', '', '', '', '', '']);
      } else if (err.code === 'account_exists') {
        goToLogin();
        return;
      }
      setError(err.message || 'Something went wrong. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleOtpChange = (index, value) => {
    // A pasted code fills every box at once.
    if (value.length > 1) {
      const digits = value.replace(/\D/g, '').slice(0, 6);
      const newOtp = [...otp];
      digits.split('').forEach((d, i) => { if (index + i < 6) newOtp[index + i] = d; });
      setOtp(newOtp);
      otpRefs.current[Math.min(index + digits.length, 5)]?.focus();
      return;
    }
    if (!/^\d*$/.test(value)) return;
    const newOtp = [...otp];
    newOtp[index] = value;
    setOtp(newOtp);
    setError(null);
    if (value && index < 5) otpRefs.current[index + 1]?.focus();
  };

  const handleOtpKeyDown = (index, e) => {
    if (e.key === 'Backspace' && !otp[index] && index > 0) {
      otpRefs.current[index - 1]?.focus();
    }
    if (e.key === 'Enter' && otp.join('').length === 6) handleVerifyCode();
  };

  const handleBack = () => {
    setError(null);
    setOtp(['', '', '', '', '', '']);
    // From the name step too: the code is spent once verified, so going back
    // means asking for a fresh one.
    if (step === 'name') setSignupToken(null);
    setStep('email');
  };

  const stepConfig = [
    { label: 'Email', key: 'email' },
    { label: 'Verify', key: 'otp' },
    { label: 'Your Name', key: 'name' },
  ];

  const currentStepIndex = stepConfig.findIndex(s => s.key === step);

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal-content guestlist-rsvp-modal" onClick={e => e.stopPropagation()}>

        {/* Header */}
        <div className="grsvp-header">
          {step !== 'email' && (
            <button className="grsvp-back-btn" onClick={handleBack} aria-label="Go back">
              <FiArrowLeft size={18} />
            </button>
          )}
          <div className="grsvp-title-area">
            <h2>RSVP to Event</h2>
            {eventTitle && <p className="grsvp-event-name">{eventTitle}</p>}
          </div>
          <button className="modal-close" onClick={onClose} aria-label="Close">
            <FiX />
          </button>
        </div>

        {/* Step Progress */}
        <div className="grsvp-steps">
          {stepConfig.map((s, i) => (
            <React.Fragment key={s.key}>
              <div className={`grsvp-step ${i < currentStepIndex ? 'done' : ''} ${i === currentStepIndex ? 'active' : ''}`}>
                <div className="grsvp-step-circle">
                  {i < currentStepIndex ? <FiCheck size={14} /> : <span>{i + 1}</span>}
                </div>
                <span className="grsvp-step-label">{s.label}</span>
              </div>
              {i < stepConfig.length - 1 && (
                <div className={`grsvp-step-line ${i < currentStepIndex ? 'done' : ''}`} />
              )}
            </React.Fragment>
          ))}
        </div>

        {/* ── STEP 1: Email ── */}
        {step === 'email' && (
          <form onSubmit={handleSendCode} className="grsvp-form">
            <p className="grsvp-subtitle">Enter your email to get started</p>

            <div className="form-group">
              <label htmlFor="rsvp-email">Email</label>
              <div className="input-with-icon">
                <FiMail className="input-icon" />
                <input
                  id="rsvp-email"
                  type="email"
                  autoComplete="email"
                  placeholder="you@example.com"
                  value={email}
                  autoFocus
                  onChange={e => { setEmail(e.target.value); setError(null); }}
                />
              </div>
              {error && <p className="error-text">{error}</p>}
            </div>

            <button type="submit" className="btn btn-primary" disabled={loading || !emailValid}>
              {loading ? 'Sending code…' : 'Send Code'}
            </button>
          </form>
        )}

        {/* ── STEP 2: Code ── */}
        {step === 'otp' && (
          <form onSubmit={handleVerifyCode} className="grsvp-form">
            <p className="grsvp-subtitle">
              Code sent to {normalisedEmail}. Check spam if it isn't there in a minute.
            </p>

            <div className="grsvp-otp-row">
              {otp.map((digit, i) => (
                <input
                  key={i}
                  ref={el => { otpRefs.current[i] = el; }}
                  type="text"
                  inputMode="numeric"
                  autoComplete={i === 0 ? 'one-time-code' : 'off'}
                  maxLength={6}
                  value={digit}
                  onChange={e => handleOtpChange(i, e.target.value)}
                  onKeyDown={e => handleOtpKeyDown(i, e)}
                  className="grsvp-otp-input"
                  autoFocus={i === 0}
                  aria-label={`Digit ${i + 1}`}
                />
              ))}
            </div>

            {error && <p className="error-text text-center">{error}</p>}

            <button type="submit" className="btn btn-primary" disabled={loading || otp.join('').length !== 6}>
              {loading ? 'Verifying…' : 'Verify'}
            </button>

            <div className="grsvp-resend">
              {resendTimer > 0 ? (
                <span className="grsvp-resend-text">Resend in {resendTimer}s</span>
              ) : (
                <>
                  <span className="grsvp-resend-text">Didn't get the code? </span>
                  <button type="button" className="grsvp-link-btn" onClick={handleSendCode} disabled={loading}>
                    Resend
                  </button>
                </>
              )}
            </div>
          </form>
        )}

        {/* ── STEP 3: Name and password ── */}
        {step === 'name' && (
          <form onSubmit={handleNameSubmit} className="grsvp-form">
            <p className="grsvp-subtitle">Almost there! Tell us your name</p>

            <div className="grsvp-name-row">
              <div className="form-group">
                <label htmlFor="rsvp-first-name">First Name</label>
                <div className="input-with-icon">
                  <FiUser className="input-icon" />
                  <input
                    id="rsvp-first-name"
                    type="text"
                    autoComplete="given-name"
                    placeholder="First name"
                    value={firstName}
                    autoFocus
                    onChange={e => { setFirstName(e.target.value); setError(null); }}
                    maxLength={50}
                  />
                </div>
              </div>

              <div className="form-group">
                <label htmlFor="rsvp-last-name">Last Name <span className="grsvp-optional">(optional)</span></label>
                <div className="input-with-icon">
                  <FiUser className="input-icon" />
                  <input
                    id="rsvp-last-name"
                    type="text"
                    autoComplete="family-name"
                    placeholder="Last name"
                    value={lastName}
                    onChange={e => { setLastName(e.target.value); setError(null); }}
                    maxLength={50}
                  />
                </div>
              </div>
            </div>

            <div className="form-group">
              <label htmlFor="rsvp-password">Password</label>
              <div className="input-with-icon">
                <FiLock className="input-icon" />
                <input
                  id="rsvp-password"
                  type="password"
                  autoComplete="new-password"
                  placeholder="At least 8 characters, to sign in later"
                  value={password}
                  onChange={e => { setPassword(e.target.value); setError(null); }}
                />
              </div>
            </div>

            {error && <p className="error-text">{error}</p>}

            <button
              type="submit"
              className="btn btn-primary"
              disabled={loading || !firstName.trim() || password.length < 8}
            >
              {loading ? 'Setting up…' : 'RSVP Now'}
            </button>
          </form>
        )}

        {/* Footer note */}
        <p className="grsvp-footer-note">
          Already have an account?{' '}
          <button type="button" onClick={goToLogin} className="grsvp-link-btn">Sign in</button>
        </p>
      </div>
    </div>
  );
};

export default GuestlistRsvpModal;
